import {
  type TreatmentCategoryId,
  type TreatmentCategory,
} from '@/consts/treatmentCategories';
import {
  type AnalyticsPatient,
  type TraitsType,
} from '@/types/metrics/analyticsPatient';

const enum AnalyticsEvent {
  ACCOUNT_REGISTERED = 'Account Registered',
  ADDRESS_SUGGESTION_SELECTED = 'Address Suggestion Selected',
  APPLE_PAY_CANCELED = 'Apple Pay Canceled',
  APPLE_PAY_CLICKED = 'Apple Pay Clicked',
  APPLE_PAY_FAILED = 'Apple Pay Failed',
  APPLE_PAY_SHOWN = 'Apple Pay Shown',
  APPLE_PAY_SUCCEEDED = 'Apple Pay Succeeded',
  BRAND_TRANSFER_DECLINED = 'Brand Transfer Declined',
  CARD_SAVED = 'Card Saved',
  CHECKOUT_CARD_EDIT_BUTTON_CLICKED = 'Checkout Card Edit Button Clicked',
  CHECKOUT_ADDRESS_EDIT_BUTTON_CLICKED = 'Checkout Address Edit Button Clicked',
  CHECKOUT_CONTINUE_BUTTON_CLICKED = 'Checkout Continue Button Clicked',
  CHECKOUT_PRICE_DROPDOWN_CLOSED = 'Checkout Price Dropdown Closed',
  CHECKOUT_PRICE_DROPDOWN_OPENED = 'Checkout Price Dropdown Opened',
  COOKIE_BANNER_ACCEPTED = 'Cookie Banner Accepted',
  COOKIE_BANNER_CLOSED = 'Cookie Banner Closed',
  CREDIT_CARD_CONTINUE_BUTTON_CLICKED = 'Credit Card Continue Button Clicked',
  CREDIT_CARD_VALIDATION_ERROR = 'Credit Card Validation Error',
  ELIGIBILITY_FAILED = 'Eligibility Failed',
  ELIGIBILITY_SUBMITTED = 'Eligibility Submitted',
  ELIGIBILITY_SUCCEEDED = 'Eligibility Succeeded',
  EMAIL_SUGGESTION_RENDERED = 'Email Suggestion Rendered',
  EMAIL_SUGGESTION_TAKEN = 'Email Suggestion Taken',
  EXPRESS_CHECKOUT_CLICKED = 'Express Checkout Clicked',
  EXPRESS_CHECKOUT_PHONE_CONFIRMATION_CODE_ENTERED = 'Express Checkout Phone Confirmation Code Entered',
  FIELD_ENTERED = 'Field Entered',
  FIELD_FOCUSED = 'Field Focused',
  FIELD_PRE_POPULATED = 'Field Pre-Populated',
  FLAG_EVALUATED = 'Flag Evaluated',
  GOOGLE_ANALYTICS_TRANSACTION = 'Google Analytics Transaction',
  GO_TO_DASHBOARD_CLICKED = 'Go To Account Dashboard Clicked',
  HOW_IT_WORKS_COMPLETED = 'How It Works Completed',
  IDENTIFY = 'Identify',
  INACTIVE_SUBSCRIPTION_FOUND_CHECKOUT = 'Inactive Subscription Found Checkout',
  INGREDIENT_ACCORDION_CLICKED = 'Ingredient Accordion Clicked',
  INVALID_VALUE_ENTERED = 'Invalid Value Entered',
  LEGAL_MODAL_OPENED = 'Legal Modal Opened',
  LEGAL_NAME_PAGE_COMPLETED = 'Legal Name Page Completed',
  LOGOUT = 'Logout',
  MEDICAL_HISTORY_SURVEY_STARTED = 'Medical History Survey Started',
  MEDICAL_HISTORY_SURVEY_COMPLETED = 'Medical History Survey Completed',
  MODULE_VIEWED = 'Module Viewed',
  ONETIME_REJECTION_VIEWED = 'Onetime Rejection Viewed',
  OFFER_COOKIE_SET = 'Offer Cookie Set',
  OFFER_REASSIGNED = 'Offer Reassigned',
  ORDER_COMPLETED = 'Order Completed',
  PAGE_LOADED = 'Loaded a Page',
  PATIENT_NAME_EDIT_BUTTON_CLICKED = 'Patient Name Edit Button Clicked',
  PATIENT_NAME_UPDATE_FAILED = 'Patient Name Update Failed',
  PAYMENT_PROCESSOR_LOAD_ERROR = 'Payment Processor Load Error',
  PAYPAL_ACCOUNT_LINKED = 'Paypal Account Linked',
  PAYPAL_BUTTON_LOADED = 'PayPal Button Loaded',
  PAYPAL_EXPRESS_CHECKOUT_LOADED = 'PayPal Express Checkout Loaded',
  PAYPAL_VALIDATION_ERROR = 'Paypal Validation Error',
  PHONE_NUMBER_CONFIRMATION_STARTED = 'Phone Number Confirmation Started',
  PHOTO_ID_UPLOAD_COMPLETED = 'Photo ID Upload Completed',
  PHOTO_REMOVED = 'Photo Removed',
  PHOTO_UPLOAD_COMPLETED = 'Photo Upload Completed',
  PHOTO_UPLOAD_SMS_LINK_SENT = 'Photo Upload SMS Link Sent',
  PHOTO_UPLOADED = 'Photo Uploaded',
  PHOTO_UPLOAD_FAILED = 'Photo Upload Failed',
  PROCEED_TO_CHECKOUT_BUTTON_CLICKED = 'Proceed to Checkout Button Clicked',
  PRODUCT_ADDED = 'Product Added',
  PRODUCT_CARD_CAROUSEL_SWIPED = 'Product Card Carousel Swiped',
  PRODUCT_ELIGIBILITY_SUBMITTED = 'Product Eligibility Submitted',
  PRODUCT_OPTION_SELECTED = 'Product Option Selected',
  PRODUCT_REMOVED = 'Product Removed',
  PROMO_CODE_ENTERED = 'Promo Code Entered',
  PROMO_CODE_MANUALLY_APPLIED = 'Promo Code Manually Applied',
  PROMO_CODE_TOGGLED = 'Promo Code Toggled',
  PROMO_CODE_VIEWED = 'Promo Code Viewed',
  PURCHASE_CONTEXT_TOGGLE_CLICKED = 'Purchase Context Toggle Clicked',
  QUESTION_ANSWERED = 'Question Answered',
  QUESTION_DISPLAYED = 'Question Displayed',
  RECOMMENDATION_DETAILS_VIEWED = 'Recommendation Details Viewed',
  RECOMMENDATION_LOADING_STARTED = 'Recommendation Loading Started',
  REGISTER_FORM_ERROR = 'Register Form Error',
  REMOVE_RX_BUTTON_CLICKED = 'Remove Rx Product From Cart And Go To Shop Clicked',
  RESUBSCRIBE_CLICKED = 'Resubscribe Clicked',
  RESUBSCRIBE_INFORMATION_UPDATED = 'Resubscribe Information Updated',
  RESUBSCRIBE_INFORMATION_UPDATE_FAILED = 'Resubscribe Information Update Failed',
  SMS_VERIFICATION_PAGE_COMPLETED = 'SMS Verification Page Completed',
  SURVEY_COMPLETED = 'Survey Completed',
  SURVEY_STARTED = 'Survey Started',
  TREATMENT_CATEGORY_SELECTED = 'Treatment Category Selected',
  UPSELL_VIEWED = 'Upsell Viewed',
  UPSELL_ACCEPTED = 'Upsell Accepted',
  UPSELL_DECLINED = 'Upsell Declined',
  UPSELL_FAILED = 'Upsell Failed',
}

type AnalyticsPayload = {
  [AnalyticsEvent.ACCOUNT_REGISTERED]: {
    fbEventId: string;
    flow?: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
  };
  [AnalyticsEvent.ADDRESS_SUGGESTION_SELECTED]: {
    flow?: string;
  };
  [AnalyticsEvent.APPLE_PAY_CANCELED]: {
    flow?: string;
  };
  [AnalyticsEvent.APPLE_PAY_CLICKED]: {
    flow?: string;
  };
  [AnalyticsEvent.APPLE_PAY_FAILED]: {
    flow?: string;
  };
  [AnalyticsEvent.APPLE_PAY_SHOWN]: {
    flow?: string;
  };
  [AnalyticsEvent.APPLE_PAY_SUCCEEDED]: {
    flow?: string;
  };
  [AnalyticsEvent.BRAND_TRANSFER_DECLINED]: {
    user_id: number;
    original_brand_id: number;
    transferred_brand_id: number;
    original_brand_status: 'active' | 'inactive';
  };
  [AnalyticsEvent.CARD_SAVED]: {
    card_type: string;
    flow?: string;
  };
  [AnalyticsEvent.CHECKOUT_CARD_EDIT_BUTTON_CLICKED]: {
    card: string;
  };
  [AnalyticsEvent.CHECKOUT_CONTINUE_BUTTON_CLICKED]: {
    card: string;
    flow?: string;
  };
  [AnalyticsEvent.CHECKOUT_PRICE_DROPDOWN_CLOSED]: {
    flow?: string;
    offerId?: number;
  };
  [AnalyticsEvent.CHECKOUT_PRICE_DROPDOWN_OPENED]: {
    flow?: string;
    offerId?: number;
  };
  [AnalyticsEvent.COOKIE_BANNER_ACCEPTED]: {
    path: string;
    module: string;
  };
  [AnalyticsEvent.COOKIE_BANNER_CLOSED]: {
    path: string;
    module: string;
  };
  [AnalyticsEvent.CREDIT_CARD_CONTINUE_BUTTON_CLICKED]: {
    flow?: string;
    offerId?: number;
  };
  [AnalyticsEvent.ELIGIBILITY_FAILED]: {
    reason: string;
    dob?: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
    funnelId: string;
  };
  [AnalyticsEvent.ELIGIBILITY_SUBMITTED]: {
    brand: string;
    flow?: string;
    funnelId: string;
    path: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
  };
  [AnalyticsEvent.ELIGIBILITY_SUCCEEDED]: {
    treatmentCategory: TreatmentCategory;
    canConsentToMedicalTreatment: boolean | undefined;
  };
  [AnalyticsEvent.EMAIL_SUGGESTION_RENDERED]: {
    emailEntered: string;
    emailSuggestion: string;
  };
  [AnalyticsEvent.EXPRESS_CHECKOUT_CLICKED]: {
    flow?: string;
    paymentMethod: string;
  };
  [AnalyticsEvent.FIELD_ENTERED]: {
    field: string;
    orderNumber?: number;
    value?: string;
  };
  [AnalyticsEvent.FIELD_FOCUSED]: {
    field: string;
    orderNumber?: number;
    value?: string;
  };
  [AnalyticsEvent.FIELD_PRE_POPULATED]: {
    city?: string;
    state?: string;
    zipcode?: string;
  };
  [AnalyticsEvent.FLAG_EVALUATED]: {
    treatmentCategory?: string;
    flag?: string;
    variation?: boolean;
  };
  [AnalyticsEvent.GOOGLE_ANALYTICS_TRANSACTION]: {
    flow?: string;
    transactionId: string | number;
    transactionProducts: {
      name: string;
      price: string;
      quantity: number;
      sku: string;
    }[];
    transactionTotal: string;
  };
  [AnalyticsEvent.IDENTIFY]: {
    patient: AnalyticsPatient;
    traits: TraitsType;
  };
  [AnalyticsEvent.INACTIVE_SUBSCRIPTION_FOUND_CHECKOUT]: {
    email: string;
    patientId: number;
    ldUuid?: string;
  };
  [AnalyticsEvent.INGREDIENT_ACCORDION_CLICKED]: {
    isOpen: boolean;
  };
  [AnalyticsEvent.INVALID_VALUE_ENTERED]: {
    name: string;
    value: string;
  };
  [AnalyticsEvent.LEGAL_MODAL_OPENED]: {
    type: string;
  };
  [AnalyticsEvent.LEGAL_NAME_PAGE_COMPLETED]: {
    userId?: number;
  };
  [AnalyticsEvent.MODULE_VIEWED]: {
    path: string;
    module: string;
  };
  [AnalyticsEvent.MEDICAL_HISTORY_SURVEY_STARTED]: {
    patientId: number | undefined;
  };
  [AnalyticsEvent.MEDICAL_HISTORY_SURVEY_COMPLETED]: {
    patientId: number | undefined;
  };
  [AnalyticsEvent.OFFER_COOKIE_SET]: {
    id: number;
    brandId: number;
    name: string;
    weight: number;
    startAt: string;
    endAt: string;
  };
  [AnalyticsEvent.ONETIME_REJECTION_VIEWED]: {
    reason: string;
  };
  [AnalyticsEvent.ORDER_COMPLETED]: {
    currency: string;
    flow?: string;
    invoiceId: number | string;
    patient: object;
    products: {
      id: number;
      name: string;
    }[];
    referrerSurveyResponse: string | null;
    revenue: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
    funnelId?: string;
  };
  [AnalyticsEvent.PAGE_LOADED]: {
    flow?: string;
    host: string;
    name: string;
    path: string;
    referrer: string;
    search: string;
    treatmentCategories?: TreatmentCategory;
    treatmentCategoryIds?: TreatmentCategoryId;
    url: string;
  };
  [AnalyticsEvent.PAYMENT_PROCESSOR_LOAD_ERROR]: {
    error: string | Event;
  };
  [AnalyticsEvent.PAYPAL_ACCOUNT_LINKED]: {
    flow?: string;
  };
  [AnalyticsEvent.PAYPAL_EXPRESS_CHECKOUT_LOADED]: {
    flow?: string;
  };
  [AnalyticsEvent.PHOTO_ID_UPLOAD_COMPLETED]: {
    brandId: number;
    treatmentCategory: TreatmentCategory;
    treatmentCategoryId: TreatmentCategoryId;
    userId?: number;
  };
  [AnalyticsEvent.PHOTO_REMOVED]: {
    type?: string;
  };
  [AnalyticsEvent.PHOTO_UPLOAD_COMPLETED]: {
    brandId: number;
    treatmentCategory: TreatmentCategory;
    treatmentCategoryId: TreatmentCategoryId;
    userId?: number;
  };
  [AnalyticsEvent.PHOTO_UPLOADED]: {
    type: string;
  };
  [AnalyticsEvent.PROCEED_TO_CHECKOUT_BUTTON_CLICKED]: {
    flow?: string;
  };
  [AnalyticsEvent.PRODUCT_ADDED]: {
    flow?: string;
    offerId?: number;
    productName: string;
  };
  [AnalyticsEvent.PRODUCT_CARD_CAROUSEL_SWIPED]: {
    flow?: string;
    imageIndex: number;
    offerId?: number;
    productName: string;
    swipeDirection: string;
  };
  [AnalyticsEvent.PRODUCT_ELIGIBILITY_SUBMITTED]: {
    brand: string;
    ineligibilityReason: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
    funnelId: string;
  };
  [AnalyticsEvent.PRODUCT_OPTION_SELECTED]: {
    offerId: number;
    selectableOption: string;
  };
  [AnalyticsEvent.PRODUCT_REMOVED]: {
    flow?: string;
    offerId?: number;
    productName: string;
  };
  [AnalyticsEvent.PROMO_CODE_ENTERED]: {
    code: string;
  };
  [AnalyticsEvent.PROMO_CODE_MANUALLY_APPLIED]: {
    promoCode: string;
  };
  [AnalyticsEvent.PROMO_CODE_TOGGLED]: {
    action: string;
  };
  [AnalyticsEvent.PURCHASE_CONTEXT_TOGGLE_CLICKED]: {
    purchaseOption: string;
  };
  [AnalyticsEvent.QUESTION_ANSWERED]: {
    question: string;
    funnelId: string;
    skipped: boolean;
  };
  [AnalyticsEvent.QUESTION_DISPLAYED]: {
    question: string;
    funnelId: string;
  };
  [AnalyticsEvent.REGISTER_FORM_ERROR]: {
    backendErrors: {
      email?: string[];
      isMismatchedBrand?: string[];
      isInvalidSparkPost?: string[];
      accountAlreadyExistsForBrand?: string[];
    };
    flow?: string;
  };
  [AnalyticsEvent.SMS_VERIFICATION_PAGE_COMPLETED]: {
    brandId: number;
    treatmentCategory: TreatmentCategory;
    treatmentCategoryId: TreatmentCategoryId;
    userId?: number;
  };
  [AnalyticsEvent.SURVEY_COMPLETED]: {
    flow?: string;
    funnelId: string;
    group: string;
    surveyEngine: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
    version: string;
  };
  [AnalyticsEvent.SURVEY_STARTED]: {
    flow?: string;
    funnelId: string;
    group: string;
    surveyEngine: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
    version: number;
  };
  [AnalyticsEvent.TREATMENT_CATEGORY_SELECTED]: {
    path: string;
    brand: string;
    treatmentCategories: TreatmentCategory;
    treatmentCategoryIds: TreatmentCategoryId;
  };
  [AnalyticsEvent.UPSELL_VIEWED]: {
    upsell: string;
  };
  [AnalyticsEvent.RESUBSCRIBE_CLICKED]: {
    purchaseContext: string;
  };
  [AnalyticsEvent.RESUBSCRIBE_INFORMATION_UPDATED]: {
    moduleName: string;
  };
  [AnalyticsEvent.RESUBSCRIBE_INFORMATION_UPDATE_FAILED]: {
    moduleName: string;
  };
};

const enum FlowTypes {
  PDP_CHECKOUT = 'PDP checkout',
}

export { AnalyticsEvent, type AnalyticsPayload, FlowTypes };
